// Physical Paint Tool in the 3D model viewport.
//
// Bridges CanvasModelViewport (three.js) and the shared physical paint engine
// (paint-engine/surface3d). The viewport keeps its own tools, projection
// strokes, layers and paint atlas; physical paint is one more surface layer on
// the model's UV atlas:
//   pointer   raycast hits on the model (subdivided along the screen path so
//             curved surfaces get every contact) -> SurfacePaintTarget
//   display   paint colour over the material's base colour, paint height as a
//             bump on the material's normal, lit by the viewport's own lights
//   history   one undo step per stroke through the Canvas history
//   saving    per model and UV layout, next to the viewport's own paint records
// The paint settings are the 2D Paint Tool's own (same panel, same presets).

import { getReadyPaintDevice } from '../../../canvas/paint-engine/integration/physicalPaintFlag';
import { buildSurfaceMap } from '../../../canvas/paint-engine/surface3d/SurfaceMap';
import {
  createSurfacePaintTarget,
  modelSimSize,
  HEIGHT_RANGE,
  DEFAULT_MODEL_SURFACE,
  MODEL_SURFACE_PRESETS,
} from '../../../canvas/paint-engine/surface3d/SurfacePaintTarget';
import { getPaintSettings, normalisePaintSettings } from './paintBrushUtils';

const SAVE_IDLE_MS = 1500;
// Physical 3D strokes made on each model this session. The Canvas history
// stores these counts; a new viewport mount (2D/3D switch) continues from
// them, so older history entries never point into the new undo stack.
const sessionStrokeCounts = new Map();
const WET_SAVE_INTERVAL_MS = 20000;
const RENDER_INTERVAL_MS = 33;
// Screen distance between raycasts along a pointer move (curved surfaces).
const HIT_STEP_PX = 3;

export function isPhysicalPaint3dAvailable() {
  return Boolean(getReadyPaintDevice());
}

// World-space triangles of the paintable meshes, in their current UV layout.
export function collectModelSurfaceMeshes(THREE, meshes) {
  const out = [];
  const materials = [];
  const normalMatrix = new THREE.Matrix3();
  const v = new THREE.Vector3();
  meshes.forEach((mesh, meshIndex) => {
    const geometry = mesh && mesh.geometry;
    const position = geometry && geometry.attributes && geometry.attributes.position;
    const uv = geometry && geometry.attributes && geometry.attributes.uv;
    if (!position || !uv) return;
    mesh.updateMatrixWorld(true);
    normalMatrix.getNormalMatrix(mesh.matrixWorld);
    const count = position.count;
    const positions = new Float32Array(count * 3);
    const uvs = new Float32Array(count * 2);
    for (let i = 0; i < count; i += 1) {
      v.fromBufferAttribute(position, i).applyMatrix4(mesh.matrixWorld);
      positions[3 * i] = v.x; positions[3 * i + 1] = v.y; positions[3 * i + 2] = v.z;
      uvs[2 * i] = uv.getX(i); uvs[2 * i + 1] = uv.getY(i);
    }
    let normals = null;
    if (geometry.attributes.normal) {
      normals = new Float32Array(count * 3);
      for (let i = 0; i < count; i += 1) {
        v.fromBufferAttribute(geometry.attributes.normal, i).applyMatrix3(normalMatrix).normalize();
        normals[3 * i] = v.x; normals[3 * i + 1] = v.y; normals[3 * i + 2] = v.z;
      }
    }
    const indices = geometry.index ? geometry.index.array : null;
    const indexCount = indices ? indices.length : count;
    const meshMaterials = Array.isArray(mesh.userData?.voxemeraSourceMaterial || mesh.material)
      ? (mesh.userData?.voxemeraSourceMaterial || mesh.material)
      : [mesh.userData?.voxemeraSourceMaterial || mesh.material];
    const materialId = (materialIndex) => {
      const source = meshMaterials[materialIndex] || meshMaterials[0];
      const label = String(source?.name || '').trim() || `${mesh.name || `Mesh ${meshIndex + 1}`}${meshMaterials.length > 1 ? ` #${materialIndex + 1}` : ''}`;
      let id = materials.findIndex((entry) => entry.source === source && entry.label === label);
      if (id < 0) { id = materials.length; materials.push({ source, label }); }
      return id;
    };
    const ranges = geometry.groups && geometry.groups.length
      ? geometry.groups.map((group) => ({ start: group.start, count: group.count, material: materialId(group.materialIndex || 0) }))
      : [{ start: 0, count: indexCount, material: materialId(0) }];
    out.push({ positions, normals, uvs, indices, materials: ranges });
  });
  return { meshes: out, materials: materials.map((entry, id) => ({ id, label: entry.label })) };
}

// Material shader addition: paint colour over the base colour, paint height
// bump on the normal, glossier roughness under paint. Uses UV channel 0 (the
// channel the Canvas 3D paint atlas uses).
export function attachPhysicalPaintOverlay(material, uniforms) {
  if (!material || material.userData?.vxPhysical3d === uniforms) return false;
  const previous = material.onBeforeCompile;
  const previousKey = material.customProgramCacheKey;
  const hadUseUv = Boolean(material.defines && Object.prototype.hasOwnProperty.call(material.defines, 'USE_UV'));
  material.userData = {
    ...(material.userData || {}),
    vxPhysical3d: uniforms,
    // Lets detachPhysicalPaintOverlay restore the material exactly.
    vxPhysical3dRestore: { previous, previousKey, hadUseUv },
  };
  material.defines = { ...(material.defines || {}), USE_UV: '' };
  material.onBeforeCompile = function onBeforeCompile(shader, renderer) {
    if (typeof previous === 'function') previous.call(this, shader, renderer);
    Object.assign(shader.uniforms, uniforms);
    const declarations = [
      'uniform sampler2D vxPhysColour;',
      'uniform sampler2D vxPhysHeight;',
      'uniform float vxPhysBump;',
      'uniform float vxPhysOn;',
      'vec4 vxPaint = vec4(0.0);',
      // sRGB decode written out (three r169 has no shader EOTF helper).
      'vec3 vxSrgbToLinear(vec3 c) { return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(vec3(0.04045), c)); }',
    ].join('\n');
    let fs = shader.fragmentShader.replace('#include <common>', `#include <common>\n${declarations}`);
    fs = fs.replace('#include <map_fragment>', `#include <map_fragment>
if (vxPhysOn > 0.5) {
  vxPaint = texture2D(vxPhysColour, vUv);
  if (vxPaint.a > 0.002) {
    vec3 vxPaintColour = vxSrgbToLinear(clamp(vxPaint.rgb / vxPaint.a, 0.0, 1.0));
    diffuseColor.rgb = mix(diffuseColor.rgb, vxPaintColour, vxPaint.a);
  }
}`);
    if (fs.includes('#include <roughnessmap_fragment>')) {
      fs = fs.replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
roughnessFactor = mix(roughnessFactor, min(roughnessFactor, 0.55), vxPaint.a);`);
    }
    if (fs.includes('#include <normal_fragment_maps>')) {
      // Height-field bump on the shading normal, from screen-space derivatives
      // of the surface position and of the paint height.
      fs = fs.replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
if (vxPhysOn > 0.5 && vxPhysBump > 0.0) {
  vec2 vxdx = dFdx(vUv);
  vec2 vxdy = dFdy(vUv);
  float vxh = texture2D(vxPhysHeight, vUv).r;
  float vxhx = (texture2D(vxPhysHeight, vUv + vxdx).r - vxh) * vxPhysBump;
  float vxhy = (texture2D(vxPhysHeight, vUv + vxdy).r - vxh) * vxPhysBump;
  vec3 vxSx = dFdx(-vViewPosition);
  vec3 vxSy = dFdy(-vViewPosition);
  vec3 vxR1 = cross(vxSy, normal);
  vec3 vxR2 = cross(normal, vxSx);
  float vxDet = dot(vxSx, vxR1) * faceDirection;
  vec3 vxGrad = sign(vxDet) * (vxhx * vxR1 + vxhy * vxR2);
  normal = normalize(abs(vxDet) * normal - vxGrad);
}`);
    }
    shader.fragmentShader = fs;
  };
  material.customProgramCacheKey = function customProgramCacheKey() {
    return `${typeof previousKey === 'function' ? previousKey.call(this) : ''}|vx-physical-3d`;
  };
  material.needsUpdate = true;
  return true;
}

// Puts a material back exactly as it was before attachPhysicalPaintOverlay.
export function detachPhysicalPaintOverlay(material) {
  const restore = material && material.userData && material.userData.vxPhysical3dRestore;
  if (!restore) return false;
  const proto = Object.getPrototypeOf(material);
  if (restore.previous === proto.onBeforeCompile) delete material.onBeforeCompile;
  else material.onBeforeCompile = restore.previous;
  if (restore.previousKey === proto.customProgramCacheKey) delete material.customProgramCacheKey;
  else material.customProgramCacheKey = restore.previousKey;
  if (!restore.hadUseUv && material.defines) {
    const defines = { ...material.defines };
    delete defines.USE_UV;
    material.defines = defines;
  }
  const userData = { ...material.userData };
  delete userData.vxPhysical3d;
  delete userData.vxPhysical3dRestore;
  material.userData = userData;
  material.needsUpdate = true;
  return true;
}

// options: THREE, renderer, getModel, getMeshes, getSession, getHitAt(x, y, rect, pressure),
//          getBrushWorldDiameter(size), getPaintSettings(), getCacheKey(),
//          saveRecord(key, record), loadRecord(key), onStrokeCommitted(count), requestRender()
export function createModelPhysicalPaint(options) {
  const { THREE, renderer } = options;
  let target = null;
  let targetKey = '';
  let materials = [];
  let restoreTried = '';
  let pointer = null; // { id, lastX, lastY }
  let lastFrame = 0;
  let lastRender = 0;
  let saveTimer = 0;
  let lastWetSave = 0;
  let saving = false;
  let destroyed = false;
  let brushScale = 1;
  let impasto = 1;
  let paintableMaterial = -1;
  let lastBuildMs = 0;
  let lastStrokeTiming = null;
  let lastSettingsRead = 0;
  let lastDown = null;
  let strokeOffset = 0;
  const countNow = () => (target
    ? strokeOffset + target.strokeCount()
    : (sessionStrokeCounts.get(options.getCacheKey()) || 0));
  const rememberCount = () => {
    if (target && targetKey) sessionStrokeCounts.set(targetKey, strokeOffset + target.strokeCount());
  };
  const pendingBitmaps = [];

  const makeTexture = () => {
    const texture = new THREE.Texture();
    texture.flipY = false;
    texture.colorSpace = THREE.NoColorSpace;
    texture.premultiplyAlpha = false;
    texture.generateMipmaps = false;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.onUpdate = () => {
      // The previous bitmaps are no longer needed once this one is on the GPU.
      while (pendingBitmaps.length > 2) {
        const old = pendingBitmaps.shift();
        try { old.close(); } catch (error) { /* already closed */ }
      }
    };
    return texture;
  };
  const colourTexture = makeTexture();
  const heightTexture = makeTexture();
  const uniforms = {
    vxPhysColour: { value: colourTexture },
    vxPhysHeight: { value: heightTexture },
    vxPhysBump: { value: 0 },
    vxPhysOn: { value: 0 },
  };

  const publish = () => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('vx-paint3d-state', {
      detail: {
        active: true,
        surface: target ? target.getSurface() : DEFAULT_MODEL_SURFACE,
        surfaces: MODEL_SURFACE_PRESETS,
        brushScale,
        impasto,
        paintableMaterial,
        materials,
        stats: target ? {
          grid: `${target.width}×${target.height}`,
          islands: target.map.stats.islands,
          coverage: target.map.stats.coverage,
          overlapFraction: target.map.stats.overlapFraction,
          outOfRangeTriangles: target.map.stats.outOfRangeTriangles,
          strokes: target.strokeCount(),
        } : null,
      },
    }));
  };

  const patched = new Set();
  let overlayFailed = false;
  const detachAll = () => {
    patched.forEach((material) => detachPhysicalPaintOverlay(material));
    patched.clear();
  };
  const attachToModel = () => {
    if (overlayFailed) return;
    const meshes = options.getMeshes();
    meshes.forEach((mesh) => {
      const list = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      list.forEach((material) => {
        if (attachPhysicalPaintOverlay(material, uniforms)) patched.add(material);
      });
    });
  };
  // If the overlay ever fails to compile on this GPU/driver, remove it at once
  // so the model keeps rendering (with its own materials) instead of vanishing.
  const previousShaderErrorHandler = renderer.debug ? renderer.debug.onShaderError : null;
  if (renderer.debug) {
    renderer.debug.onShaderError = function onShaderError(gl, program, vertexShader, fragmentShader) {
      const source = (fragmentShader && gl.getShaderSource(fragmentShader)) || '';
      if (source.includes('vxPhysColour')) {
        overlayFailed = true;
        console.error('[3D physical paint] material overlay failed to compile and was removed:', gl.getShaderInfoLog(fragmentShader));
        detachAll();
        uniforms.vxPhysOn.value = 0;
      }
      if (typeof previousShaderErrorHandler === 'function') {
        previousShaderErrorHandler.call(this, gl, program, vertexShader, fragmentShader);
      } else {
        console.error('THREE.WebGLProgram: shader error', gl.getProgramInfoLog(program), gl.getShaderInfoLog(vertexShader), gl.getShaderInfoLog(fragmentShader));
      }
    };
  }

  // Builds (or rebuilds after a UV layout change) the paint target for the model.
  const ensureTarget = () => {
    const gpu = getReadyPaintDevice();
    const session = options.getSession();
    const key = options.getCacheKey();
    if (!gpu || !session || !session.canvas || !key) return null;
    if (target && targetKey === key) return target;
    if (target) { saveNow(); target.destroy(); target = null; }
    const t0 = performance.now();
    const { width, height } = modelSimSize(session.canvas.width, session.canvas.height);
    const collected = collectModelSurfaceMeshes(THREE, options.getMeshes());
    materials = collected.materials;
    const map = buildSurfaceMap(collected.meshes, { width, height });
    if (!map.stats.coveredCells) return null;
    target = createSurfacePaintTarget(gpu, map, { surface: DEFAULT_MODEL_SURFACE });
    targetKey = key;
    strokeOffset = sessionStrokeCounts.get(key) || 0;
    uniforms.vxPhysBump.value = HEIGHT_RANGE * map.texelWorld * impasto;
    lastBuildMs = performance.now() - t0;
    attachToModel();
    publish();
    return target;
  };

  const uploadMaps = () => {
    const { colour, height } = target.renderMaps();
    pendingBitmaps.push(colour, height);
    colourTexture.image = colour;
    heightTexture.image = height;
    colourTexture.needsUpdate = true;
    heightTexture.needsUpdate = true;
    uniforms.vxPhysOn.value = 1;
  };

  // ---------------------------------------------------------------- persistence
  const saveNow = async () => {
    if (!target || !target.hasPaint() || saving || !targetKey) return false;
    saving = true;
    const key = targetKey;
    const owner = target;
    try {
      const state = await owner.exportState();
      if (state) await options.saveRecord(`${key}|physical3d`, { kind: 'physical3d', savedAt: Date.now(), state });
      return true;
    } catch (error) {
      console.warn('[3D physical paint] save failed', error);
      return false;
    } finally {
      saving = false;
    }
  };
  const scheduleSave = (delay = SAVE_IDLE_MS) => {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => { saveTimer = 0; saveNow(); }, delay);
  };
  const tryRestore = async () => {
    const key = options.getCacheKey();
    if (!key || restoreTried === key || !getReadyPaintDevice() || !options.getSession()) return;
    restoreTried = key;
    try {
      const record = await options.loadRecord(`${key}|physical3d`);
      if (!record || !record.state || destroyed) return;
      const t = ensureTarget();
      if (t && t.importState(record.state)) {
        uploadMaps();
        options.requestRender?.();
        publish();
      }
    } catch (error) {
      console.warn('[3D physical paint] restore failed', error);
    }
  };

  // ---------------------------------------------------------------- pointer
  const settingsNow = () => {
    const supplied = options.getPaintSettings() || {};
    return normalisePaintSettings({ ...getPaintSettings(), ...supplied });
  };
  const hitFromEvent = (event, x, y, rect) => {
    const pressure = event.pointerType === 'mouse' ? 0 : Number(event.pressure) || 0;
    const hit = options.getHitAt(x, y, rect, pressure);
    if (!hit) return null;
    return {
      point: [hit.point.x, hit.point.y, hit.point.z],
      normal: [hit.normal.x, hit.normal.y, hit.normal.z],
      uv: [hit.uv.x, hit.uv.y],
      pressure,
      pointerType: event.pointerType || 'mouse',
      tiltX: Number(event.tiltX) || 0,
      tiltY: Number(event.tiltY) || 0,
      twist: Number(event.twist) || 0,
      time: performance.now(),
    };
  };

  function pointerDown(event) {
    if (destroyed || event.button !== 0) return false;
    const t = ensureTarget();
    if (!t) return false;
    const rect = renderer.domElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const hit = hitFromEvent(event, x, y, rect);
    lastDown = { x: Math.round(x), y: Math.round(y), width: Math.round(rect.width), height: Math.round(rect.height), hit: Boolean(hit), uv: hit ? hit.uv : null };
    const settings = settingsNow();
    const worldDiameter = options.getBrushWorldDiameter(Math.max(1, Number(settings.toolSize) || 70)) * brushScale;
    const stroke = { settings, brushWorldRadius: worldDiameter / 2, strokeId: `paint3d-${Date.now()}`, paintableMaterial };
    const t0 = performance.now();
    // A stroke may start beside the model: it begins where it first reaches the surface.
    if (hit) t.beginStroke({ ...stroke, hit });
    lastStrokeTiming = { down: performance.now() - t0, moves: [], up: 0 };
    pointer = { id: event.pointerId, lastX: x, lastY: y, pending: hit ? null : stroke };
    try { renderer.domElement.setPointerCapture(event.pointerId); } catch (error) { /* not capturable */ }
    return true;
  }

  function pointerMove(event) {
    if (!pointer || event.pointerId !== pointer.id || !target) return false;
    const rect = renderer.domElement.getBoundingClientRect();
    const events = typeof event.getCoalescedEvents === 'function' ? event.getCoalescedEvents() : [];
    const list = events.length ? events : [event];
    const t0 = performance.now();
    for (const e of list) {
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const dist = Math.hypot(x - pointer.lastX, y - pointer.lastY);
      const steps = Math.max(1, Math.ceil(dist / HIT_STEP_PX));
      for (let s = 1; s <= steps; s += 1) {
        const f = s / steps;
        const hit = hitFromEvent(e, pointer.lastX + (x - pointer.lastX) * f, pointer.lastY + (y - pointer.lastY) * f, rect);
        if (!hit) continue;
        if (pointer.pending) {
          target.beginStroke({ ...pointer.pending, hit });
          pointer.pending = null;
        } else target.strokeTo(hit);
      }
      pointer.lastX = x;
      pointer.lastY = y;
    }
    if (lastStrokeTiming && lastStrokeTiming.moves.length < 5000) lastStrokeTiming.moves.push(performance.now() - t0);
    return true;
  }

  function pointerUp(event) {
    if (!pointer || (event && event.pointerId !== pointer.id)) return false;
    pointer = null;
    if (!target) return true;
    const t0 = performance.now();
    const painted = target.endStroke();
    if (lastStrokeTiming) lastStrokeTiming.up = performance.now() - t0;
    if (painted) {
      scheduleSave();
      rememberCount();
      if (typeof options.onStrokeCommitted === 'function') options.onStrokeCommitted(countNow());
      publish();
    }
    return true;
  }

  // ---------------------------------------------------------------- frame
  function frame(now = performance.now()) {
    if (destroyed) return;
    if (!target) { tryRestore(); return; }
    if (targetKey !== options.getCacheKey() && options.getCacheKey()) {
      // UV layout or model changed: keep this paint saved under its own key.
      saveNow();
      target.destroy();
      target = null;
      targetKey = '';
      uniforms.vxPhysOn.value = 0;
      return;
    }
    const dt = lastFrame ? (now - lastFrame) / 1000 : 0;
    lastFrame = now;
    attachToModel();
    // Raised paint uses the Paint Tool's own Impasto depth setting.
    if (now - lastSettingsRead > 250) {
      lastSettingsRead = now;
      const physical = (options.getPaintSettings() || {}).physical || {};
      const depth = Number.isFinite(Number(physical.impastoDepth)) ? Number(physical.impastoDepth) : 1;
      uniforms.vxPhysBump.value = HEIGHT_RANGE * target.map.texelWorld * depth * impasto;
    }
    const dirty = target.advance(dt, now);
    if (dirty && (target.isStroking() || now - lastRender >= RENDER_INTERVAL_MS)) {
      uploadMaps();
      lastRender = now;
    }
    if (target.isWet() && !target.isStroking() && now - lastWetSave > WET_SAVE_INTERVAL_MS && !saveTimer) {
      lastWetSave = now;
      scheduleSave(0);
    }
  }

  // ---------------------------------------------------------------- 3D controls
  const onSet = (event) => {
    const d = (event && event.detail) || {};
    if (Number.isFinite(Number(d.brushScale))) brushScale = Math.max(0.1, Math.min(8, Number(d.brushScale)));
    if (Number.isFinite(Number(d.impasto))) {
      impasto = Math.max(0, Math.min(4, Number(d.impasto)));
      if (target) uniforms.vxPhysBump.value = HEIGHT_RANGE * target.map.texelWorld * impasto;
    }
    if (Number.isFinite(Number(d.paintableMaterial))) paintableMaterial = Number(d.paintableMaterial);
    if (d.surface) {
      const t = ensureTarget();
      if (t && t.setSurface(d.surface)) scheduleSave();
    }
    publish();
  };
  const onQuery = () => publish();
  if (typeof window !== 'undefined') {
    window.addEventListener('vx-paint3d-set', onSet);
    window.addEventListener('vx-paint3d-query', onQuery);
    publish();
  }

  function dispose() {
    if (destroyed) return;
    destroyed = true;
    if (saveTimer) clearTimeout(saveTimer);
    if (typeof window !== 'undefined') {
      window.removeEventListener('vx-paint3d-set', onSet);
      window.removeEventListener('vx-paint3d-query', onQuery);
      window.dispatchEvent(new CustomEvent('vx-paint3d-state', { detail: { active: false } }));
    }
    const owner = target;
    target = null;
    if (owner) {
      // Save before releasing the GPU state (switching to 2D, leaving the canvas).
      const finish = () => owner.destroy();
      if (owner.hasPaint() && targetKey) {
        saving = false;
        owner.exportState()
          .then((state) => (state ? options.saveRecord(`${targetKey}|physical3d`, { kind: 'physical3d', savedAt: Date.now(), state }) : null))
          .catch((error) => console.warn('[3D physical paint] save on close failed', error))
          .finally(finish);
      } else finish();
    }
    detachAll();
    if (renderer.debug) renderer.debug.onShaderError = previousShaderErrorHandler;
    pendingBitmaps.splice(0).forEach((b) => { try { b.close(); } catch (error) { /* closed */ } });
    colourTexture.dispose();
    heightTexture.dispose();
  }

  return {
    pointerDown,
    pointerMove,
    pointerUp,
    isStroking: () => Boolean(pointer),
    frame,
    strokeCount: () => countNow(),
    setStrokeCount: (count) => {
      if (!target) return countNow();
      // Strokes from before this target (another viewport mount) cannot be undone here.
      const result = strokeOffset + target.setStrokeCount(Math.max(0, Number(count) - strokeOffset));
      rememberCount();
      scheduleSave();
      publish();
      return result;
    },
    saveNow,
    debug: () => ({
      target: target ? { width: target.width, height: target.height, stats: target.stats, map: target.map.stats, texelWorld: target.map.texelWorld, gpuBytes: target.gpuBytes(), wet: target.isWet() } : null,
      lastBuildMs,
      lastDown,
      lastStrokeTiming,
      brushScale,
      impasto,
    }),
    dispose,
  };
}
